import SpotDatafeed from './SpotDatafeed';
import FuturesDatafeed from './FuturesDatafeed';
import Logger from '../../../utils/Logger';

/**
 * DatafeedFactory - Factory pattern for creating market-specific datafeeds
 * 
 * Automatically detects the market type from URL and returns the appropriate datafeed:
 * - /spot/ -> SpotDatafeed (Unocoin)
 * - /futures/ -> FuturesDatafeed (PI42)
 */
class DatafeedFactory {
  /**
   * Validate and sanitize market type
   * @param {string} market - Market type from URL
   * @returns {string} - Validated market type
   */
  static validateMarket(market) {
    const allowedMarkets = ['spot', 'futures'];
    const sanitized = (market || 'spot').toLowerCase().replace(/[^a-z]/g, '');
    
    if (!allowedMarkets.includes(sanitized)) {
      Logger.warn(`[Factory] Invalid market type: ${market}, defaulting to 'spot'`);
      return 'spot';
    }
    
    return sanitized;
  }
  
  /**
   * Validate and sanitize coin symbol
   * @param {string} coin - Coin symbol from URL
   * @param {string} defaultValue - Default value if invalid
   * @returns {string} - Validated coin symbol
   */
  static validateCoin(coin, defaultValue) {
    if (!coin) return defaultValue;
    
    // Allow only alphanumeric characters, max 10 chars
    const sanitized = coin.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10);
    
    if (!sanitized || sanitized.length < 2) {
      Logger.warn(`[Factory] Invalid coin: ${coin}, using default: ${defaultValue}`);
      return defaultValue;
    }
    
    return sanitized;
  }
  
  /**
   * Validate and sanitize theme
   * @param {string} theme - Theme from URL
   * @returns {string} - Validated theme
   */
  static validateTheme(theme) {
    const allowedThemes = ['light', 'dark'];
    const sanitized = (theme || 'dark').toLowerCase().replace(/[^a-z]/g, '');
    
    if (!allowedThemes.includes(sanitized)) {
      Logger.warn(`[Factory] Invalid theme: ${theme}, defaulting to 'dark'`);
      return 'dark';
    }
    
    return sanitized;
  }
  
  /**
   * Validate and sanitize price scale
   * @param {string|number} priceScale - Price scale from URL
   * @returns {number} - Validated price scale (0-8)
   */
  static validatePriceScale(priceScale) {
    const parsed = parseInt(priceScale, 10);
    
    if (isNaN(parsed)) {
      Logger.warn(`[Factory] Invalid priceScale: ${priceScale}, defaulting to 2`);
      return 2;
    }
    
    // Clamp to valid range [0, 8]
    if (parsed < 0) {
      Logger.warn(`[Factory] priceScale ${parsed} < 0, clamping to 0`);
      return 0;
    }
    
    if (parsed > 8) {
      Logger.warn(`[Factory] priceScale ${parsed} > 8, clamping to 8`);
      return 8;
    }
    
    return parsed;
  }
  
  /**
  /**
   * Create datafeed based on market type and configuration
   * 
   * @param {string} market - Market type ('spot' or 'futures')
   * @param {Object} config - Configuration object
   * @param {string} config.baseCoin - Base currency (e.g., 'INR')
   * @param {string} config.cryptoCoin - Crypto currency (e.g., 'BTC')
   * @param {number} config.priceScale - Decimal places for price (default: 2)
   * @param {string} config.timezone - Timezone (default: 'Asia/Kolkata')
   * @returns {BaseDatafeed} - Appropriate datafeed instance
   */
  static createDatafeed(market, config = {}) {
    const validatedMarket = DatafeedFactory.validateMarket(market);
    
    Logger.info(`[Factory] Creating datafeed for market: ${validatedMarket}`, config);

    switch (validatedMarket) {
      case 'spot':
        return new SpotDatafeed(config);
      
      case 'futures':
        return new FuturesDatafeed(config);
      
      default:
        Logger.warn(`[Factory] Unknown market type: ${market}, defaulting to Spot`);
        return new SpotDatafeed(config);
    }
  }

  /**
   * Create datafeed from URL parameters
   * 
   * Supports both new and legacy URL formats:
   * - New: /[market]/[baseCoin]/[cryptoCoin]/[theme]/[priceScale]
   *   Examples: /spot/INR/BTC/dark/4, /futures/INR/BTC/light/2
   * 
   * - Legacy (backward compatible): /[baseCoin]/[cryptoCoin]/[theme]/[priceScale]
   *   Example: /INR/BTC/light/4 (defaults to 'spot')
   * 
   * @param {string} url - Current browser URL
   * @returns {Object} - { datafeed, config, market }
   */
  static createFromURL(url = window.location.href) {
    const urlParts = url.split('/');
    
    // Detect if using new format (with market) or legacy format
    // Check if urlParts[3] is a valid market type ('spot' or 'futures')
    const possibleMarket = (urlParts[3] || '').toLowerCase();
    const isNewFormat = possibleMarket === 'spot' || possibleMarket === 'futures';
    
    let market, baseCoin, cryptoCoin, theme, priceScale;
    
    if (isNewFormat) {
      // New format: /[market]/[baseCoin]/[cryptoCoin]/[theme]/[priceScale]
      market = DatafeedFactory.validateMarket(urlParts[3]);
      baseCoin = DatafeedFactory.validateCoin(urlParts[4], 'INR');
      cryptoCoin = DatafeedFactory.validateCoin(urlParts[5], 'BTC');
      theme = DatafeedFactory.validateTheme(urlParts[6]);
      priceScale = DatafeedFactory.validatePriceScale(urlParts[7]);
    } else {
      // Legacy format: /[baseCoin]/[cryptoCoin]/[theme]/[priceScale]
      // Default to 'spot' for backward compatibility
      market = 'spot';
      baseCoin = DatafeedFactory.validateCoin(urlParts[3], 'INR');
      cryptoCoin = DatafeedFactory.validateCoin(urlParts[4], 'BTC');
      theme = DatafeedFactory.validateTheme(urlParts[5]);
      priceScale = DatafeedFactory.validatePriceScale(urlParts[6]);
      
      Logger.info('[Factory] Legacy URL format detected, defaulting to spot market');
    }

    // Get timezone from localStorage (with safe parsing)
    const timezone = DatafeedFactory.getTimeZone();

    const config = {
      baseCoin,
      cryptoCoin,
      priceScale,
      theme,
      timezone
    };

    const datafeed = DatafeedFactory.createDatafeed(market, config);

    Logger.info('[Factory] Created datafeed from URL:', {
      url,
      market,
      config
    });

    return {
      datafeed,
      config,
      market
    };
  }

  /**
   * Get timezone from localStorage with validation
   */
  static getTimeZone() {
    try {
      const countryDetail = localStorage.getItem('COUNTRY_DETAIL');
      if (!countryDetail) {
        return 'Asia/Kolkata';
      }
      
      let parsed;
      try {
        parsed = JSON.parse(countryDetail);
      } catch (parseError) {
        Logger.error('[Factory] Invalid JSON in localStorage COUNTRY_DETAIL:', parseError);
        return 'Asia/Kolkata';
      }
      
      const { timeZone } = parsed || {};
      const validTimezones = [
        'UTC', 'America/New_York', 'America/Los_Angeles', 'America/Chicago', 
        'America/Phoenix', 'America/Toronto', 'America/Vancouver', 
        'America/Argentina/Buenos_Aires', 'America/El_Salvador', 'America/Sao_Paulo',
        'America/Bogota', 'America/Caracas', 'Europe/Moscow', 'Europe/Athens',
        'Europe/Berlin', 'Europe/London', 'Europe/Madrid', 'Europe/Paris',
        'Europe/Rome', 'Europe/Warsaw', 'Europe/Istanbul', 'Europe/Zurich',
        'Australia/Sydney', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/ACT',
        'Asia/Almaty', 'Asia/Ashkhabad', 'Asia/Tokyo', 'Asia/Taipei',
        'Asia/Singapore', 'Asia/Shanghai', 'Asia/Seoul', 'Asia/Tehran',
        'Asia/Dubai', 'Asia/Kolkata', 'Asia/Hong_Kong', 'Asia/Bangkok',
        'Pacific/Auckland', 'Pacific/Chatham', 'Pacific/Fakaofo', 'Pacific/Honolulu',
        'America/Mexico_City', 'Africa/Johannesburg', 'Asia/Kathmandu', 'US/Mountain'
      ];
      
      if (timeZone && validTimezones.includes(timeZone)) {
        return timeZone;
      }
      
      Logger.warn('[Factory] Invalid timezone in localStorage:', timeZone, 'using default');
    } catch (error) {
      Logger.error('[Factory] Error reading timezone from localStorage:', error);
    }
    
    return 'Asia/Kolkata';
  }

  /**
   * Validate market type
   */
  static isValidMarket(market) {
    return ['spot', 'futures'].includes((market || '').toLowerCase());
  }

  /**
   * Get list of supported markets
   */
  static getSupportedMarkets() {
    return [
      { value: 'spot', label: 'Spot Trading', exchange: 'Unocoin' },
      { value: 'futures', label: 'Futures Trading', exchange: 'Unocoin' }
    ];
  }
}

export default DatafeedFactory;
